import { useEffect, useState } from "react";
import { Box, CircularProgress, TextareaAutosize } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ButtonComponent from "../../../components/common/button/Button";
import Modal from "../../../components/common/modal/Modal";
import TextInput from "../../../components/common/TextInput";
import {
  saveVariantEndpoint,
  viewUserVariantEndpoint,
  editUserVariantEndpoint,
  deleteUserVariantEndpoint,
  shareVariantEndpoint,
  shareVariantUsersEndpoint,
} from "../../../common/endpoints";
import { postCreateNewVariant } from "../../../redux/actions/VariantView/NewVariantSlice";
import { getVariantsList } from "../../../redux/actions/VariantView/GetVariantsListSlice";
import { editVariantAPI } from "../../../redux/actions/VariantView/EditVariantSlice";
import { deleteVariantAPI } from "../../../redux/actions/VariantView/DeleteVariantSlice";
import { shareVariantAPI } from "../../../redux/actions/VariantView/ShareVariantSlice";
import { getShareVariantUsers } from "../../../redux/actions/VariantView/ShareVariantUsersSlice";

export const VariantViewModal = ({
  openCreateVariantModal,
  handleCloseCreateVariantModal,
  data,
  tableType,
}) => {
  const dispatch = useDispatch();
  const [variantSelection, setVariantSelection] = useState("");
  const [variantsResponse, setVariantsResponse] = useState("");
  const [formDataModal, setFormDataModal] = useState({});
  const [variantsData, setVariantsData] = useState([]);
  const userEmail = localStorage.getItem("data");

  const onCloseVariantModal = () => {
    setVariantSelection("");
    handleCloseCreateVariantModal();
  };

  const onClickCancel = () => {
    handleCloseCreateVariantModal();
    setVariantSelection("");
  };

  const normalizeDimensionValue = (value) => {
    const normalized = typeof value === "string" ? value.trim() : "";
    if (!normalized) return "All";
    const lowered = normalized.toLowerCase();
    if (lowered === "undefined" || lowered === "null") return "All";
    return normalized;
  };

  const getVariantsData = async () => {
    try {
      const responseGetVariants = await dispatch(
        getVariantsList({
          dynamicEndpoint: `${viewUserVariantEndpoint}/${userEmail}`,
          params: {
            tableType,
            plant: localStorage.getItem("selectedLTPlantCode"),
          },
        })
      );

      if (responseGetVariants?.meta?.requestStatus === "fulfilled") {
        setVariantsResponse(responseGetVariants?.payload);
        setVariantsData(responseGetVariants?.payload?.variants || []);
      } else {
        console.error("Failed to fetch data:", responseGetVariants?.error);
        toast.error(responseGetVariants?.payload?.errorMessage || "Failed to load variants", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error("Failed to load variants", {
        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    if (openCreateVariantModal) {
      getVariantsData();
    }
  }, [openCreateVariantModal]);

  const handleCreateNewVariant = async () => {
    const filterData = data;
    const ltRegion = normalizeDimensionValue(localStorage.getItem("LTRegion"));
    const ltPlant = normalizeDimensionValue(localStorage.getItem("selectedLTPlantCode"));

    const reqBodyCreateVariant = {
      userEmail,
      tableType,
      plant: ltPlant,
      region: ltRegion,
      variants: [
        {
          variantName: formDataModal?.variantName,
          variantDescription: formDataModal?.variantDesc,
          filterJson: JSON.stringify(filterData),
        },
      ],
    };

    try {
      const response = await dispatch(
        postCreateNewVariant({
          dynamicEndpoint: saveVariantEndpoint,
          data: reqBodyCreateVariant,
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success(response?.payload || "Variant created", {
          autoClose: 2000,
        });
        onCloseVariantModal();
      } else {
        toast.error(response?.payload?.errorMessage || "Failed to create variant", {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const onClickVariantSelection = (name) => {
    setVariantSelection(name);
  };

  const getCreateFormData = (formData) => {
    setFormDataModal(formData);
  };

  return (
    <Modal
      className="createVariantModal_cls"
      open={openCreateVariantModal}
      handleClose={onCloseVariantModal}
      title="Variants Catalog"
      fullWidth={true}
      action={
        <CreateVariantFooter
          handleCreateNewVariant={handleCreateNewVariant}
          onClickCancel={onClickCancel}
          variantSelection={variantSelection}
          formDataModal={formDataModal}
        />
      }
      content={
        <CreateVariantContent
          onClickVariantSelection={onClickVariantSelection}
          variantSelection={variantSelection}
          data={data}
          getCreateFormData={getCreateFormData}
          isLimitReached={variantsResponse?.isLimitReached}
          limitCount={variantsResponse?.limitCount}
          variantsData={variantsData}
          onClickCancel={onClickCancel}
        />
      }
    />
  );
};

const CreateVariantFooter = ({ formDataModal, variantSelection, onClickCancel, handleCreateNewVariant }) => {
  if (variantSelection === "new") {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <ButtonComponent
          label="Cancel"
          type="primary"
          maxWidth="80px"
          onClick={onClickCancel}
        />
        <ButtonComponent
          label="Create"
          type="secondary"
          maxWidth="80px"
          onClick={handleCreateNewVariant}
          disabled={!formDataModal?.variantDesc || !formDataModal?.variantName}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <ButtonComponent
        label="Cancel"
        type="primary"
        maxWidth="80px"
        onClick={onClickCancel}
      />
    </div>
  );
};

const CreateVariantContent = ({
  onClickVariantSelection,
  variantSelection,
  getCreateFormData,
  isLimitReached,
  variantsData,
  data,
  onClickCancel,
  limitCount,
}) => {
  const NewVariantSlice = useSelector((state) => state.NewVariantSlice);
  const EditVariantSlice = useSelector((state) => state.EditVariantSlice);
  const GetVariantsListSlice = useSelector((state) => state.GetVariantsListSlice);

  const isLoading =
    NewVariantSlice?.loading ||
    EditVariantSlice?.loading ||
    GetVariantsListSlice?.loading;

  if (isLoading) {
    return (
      <Box style={{ textAlign: "center", height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress color="inherit" style={{ width: "20px", height: "20px", marginRight: 30 }} />
      </Box>
    );
  }

  if (variantSelection === "new") {
    return <CreateNewVariantComponent getCreateFormData={getCreateFormData} />;
  }

  if (variantSelection === "modify") {
    return (
      <ModifyVariantComponent
        data={data}
        variantsData={variantsData}
        onClickCancel={onClickCancel}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
      <p>Note: You can create a maximum of {limitCount} variants.</p>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 20 }}>
        <ButtonComponent
          label="Create New Variant"
          type="secondary"
          maxWidth="200px"
          onClick={() => onClickVariantSelection("new")}
          disabled={isLimitReached || !data || Object.keys(data).length === 0}
        />
        <ButtonComponent
          label="Edit Variant"
          type="secondary"
          maxWidth="150px"
          onClick={() => onClickVariantSelection("modify")}
          disabled={!data || Object.keys(data).length === 0}
        />
      </div>
      {(!data || Object.keys(data).length === 0) && (
        <p style={{ color: "red", marginBottom: "8px" }}>
          Please select filters to create variant
        </p>
      )}
    </div>
  );
};

const CreateNewVariantComponent = ({ getCreateFormData }) => {
  const [formData, setFormData] = useState({ variantName: "", variantDesc: "" });

  const onChangeVariant = (e) => {
    const next = {
      ...formData,
      [e.target.name]: e.target.value,
    };
    setFormData(next);
    getCreateFormData(next);
  };

  return (
    <div>
      <div style={{ display: "flex" }}>
        <p style={{ flex: 1 }}>Variant Name</p>
        <div style={{ flex: 3 }}>
          <TextInput
            name="variantName"
            value={formData?.variantName}
            onChange={onChangeVariant}
            width="100%"
          />
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <p style={{ flex: 1 }}>Variant Description</p>
        <div style={{ flex: 3 }}>
          <TextareaAutosize
            minRows={6}
            style={{ width: "99%", fontFamily: "sans-serif", fontSize: 16, padding: 10 }}
            name="variantDesc"
            value={formData?.variantDesc}
            onChange={onChangeVariant}
          />
        </div>
      </div>
    </div>
  );
};

const ModifyVariantComponent = ({ variantsData, data, onClickCancel }) => {
  const userEmail = localStorage.getItem("data");
  const dispatch = useDispatch();
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [editFormData, setEditFormData] = useState({ variantName: "", variantDesc: "" });

  const onClickEditButton = (item) => {
    setIsEditClicked(true);
    setEditFormData({
      variantName: item.variantName,
      variantDesc: item.variantDescription,
      userVariantMapSeqId: item.userVariantMapSeqId,
    });
  };

  const onChangeEditFormData = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  const onClickBackFromEditMode = () => {
    setIsEditClicked(false);
  };

  const onClickDeleteAPI = async (item) => {
    try {
      const response = await dispatch(
        deleteVariantAPI({
          dynamicEndpoint: `${deleteUserVariantEndpoint}/${item.userVariantMapSeqId}?userEmail=${encodeURIComponent(userEmail)}`,
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success(response?.payload || "Variant deleted", { autoClose: 2000 });
        onClickCancel();
      } else {
        toast.error(response?.payload?.message || "Failed to delete variant", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const onClickUpdateAPI = async () => {
    const reqBodyReplaceVariant = {
      userEmail,
      variantName: editFormData.variantName || "",
      variantDescription: editFormData.variantDesc || "",
      filterJson: JSON.stringify(data),
    };

    try {
      const response = await dispatch(
        editVariantAPI({
          dynamicEndpoint: `${editUserVariantEndpoint}/${editFormData.userVariantMapSeqId}`,
          data: reqBodyReplaceVariant,
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        onClickCancel();
        toast.success(response?.payload || "Variant updated", {
          autoClose: 2000,
        });
      } else {
        toast.error(response?.payload?.errorMessage || "Failed to update variant", {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  if (isEditClicked) {
    return (
      <div>
        <div>
          <div style={{ display: "flex" }}>
            <p style={{ flex: 1 }}>Variant Name</p>
            <div style={{ flex: 3 }}>
              <TextInput
                name="variantName"
                value={editFormData?.variantName}
                onChange={onChangeEditFormData}
                width="100%"
              />
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <p style={{ flex: 1 }}>Variant Description</p>
            <div style={{ flex: 3 }}>
              <TextareaAutosize
                minRows={6}
                style={{ width: "99%", padding: 10 }}
                name="variantDesc"
                value={editFormData?.variantDesc}
                onChange={onChangeEditFormData}
              />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 10 }}>
          <ButtonComponent
            label="Back"
            type="primary"
            maxWidth="80px"
            onClick={onClickBackFromEditMode}
          />
          <ButtonComponent
            label="Update"
            type="secondary"
            maxWidth="100px"
            onClick={onClickUpdateAPI}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "200px" }}>Variant Name</th>
            <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "350px" }}>Variant Description</th>
            <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>Created On</th>
            <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>Modified On</th>
            <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {variantsData.map((item) => (
            <tr key={item?.userVariantMapSeqId}>
              <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "200px", wordBreak: "break-word" }}>{item?.variantName}</td>
              <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "350px", wordBreak: "break-word" }}>{item?.variantDescription}</td>
              <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>{item?.createdOn}</td>
              <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>{item?.modifiedOn}</td>
              <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "center" }}>
                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                  <ButtonComponent
                    label="Edit"
                    type="secondary"
                    maxWidth="80px"
                    onClick={() => onClickEditButton(item)}
                  />
                  <ButtonComponent
                    label="Delete"
                    type="secondary"
                    bgColor="#e70d0d"
                    color="#ffffff"
                    maxWidth="80px"
                    onClick={() => onClickDeleteAPI(item)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
